import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useChartsData = () => {
  return useQuery({
    queryKey: ['charts-data'],
    queryFn: async () => {
      // Garantir que pegamos a data local, não UTC
      const hoje = new Date();
      const year = hoje.getFullYear();
      const month = String(hoje.getMonth() + 1).padStart(2, '0');
      const day = String(hoje.getDate()).padStart(2, '0');
      const dataHoje = `${year}-${month}-${day}`;

      const { data: agendamentos, error } = await supabase
        .from('agendamentos_robustos')
        .select('*');

      // Buscar configuração da loja para os intervalos
      const { data: configLoja } = await supabase
        .from('info_loja_public')
        .select('opening_time, closing_time, slot_interval_minutes')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching chart data:', error);
        throw error;
      }

      // Filtrar apenas dados do dia atual para os gráficos específicos
      const dadosHoje = agendamentos?.filter(item => item.DATA === dataHoje) || [];

      // Dados para distribuição por status (todos os dados)
      const statusCounts = agendamentos?.reduce((acc: any, item) => {
        acc[item.STATUS] = (acc[item.STATUS] || 0) + 1;
        return acc;
      }, {}) || {};

      const statusData = [
        { name: 'AGENDADO', value: statusCounts.AGENDADO || 0, color: '#22c55e' },
        { name: 'CANCELADO', value: statusCounts.CANCELADO || 0, color: '#ef4444' },
        { name: 'REAGENDADO', value: statusCounts.REAGENDADO || 0, color: '#f59e0b' }
      ];

      // Gerar todos os intervalos possíveis com base na configuração
      const allPossibleSlots: string[] = [];
      if (configLoja?.opening_time && configLoja?.closing_time && configLoja?.slot_interval_minutes) {
        const [startHour, startMin] = configLoja.opening_time.split(':').map(Number);
        const [endHour, endMin] = configLoja.closing_time.split(':').map(Number);
        const interval = configLoja.slot_interval_minutes;

        let current = startHour * 60 + startMin;
        const end = endHour * 60 + endMin;

        while (current < end) {
          const h = Math.floor(current / 60);
          const m = current % 60;
          allPossibleSlots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
          current += interval;
        }
      }

      // Dados para horários de pico - TODOS OS DADOS HISTÓRICOS
      const horariosPicoCounts = agendamentos
        ?.filter(item => item.STATUS === 'AGENDADO' || item.STATUS === 'REAGENDADO')
        .reduce((acc: any, item) => {
          const hora = item.HORA ? item.HORA.toString().substring(0, 5) : '';
          if (hora) {
            acc[hora] = (acc[hora] || 0) + 1;
          }
          return acc;
        }, {}) || {};

      // Preencher todos os slots possíveis, mesmo os com zero agendamentos
      const horariosPicoData = (allPossibleSlots.length > 0 ? allPossibleSlots : Object.keys(horariosPicoCounts).sort())
        .map(hora => ({
          hora: hora,
          agendamentos: horariosPicoCounts[hora] || 0
        }));

      // Dados para performance por profissional - TODOS OS DADOS HISTÓRICOS
      const profissionalCounts = agendamentos
        ?.filter(item => item.STATUS === 'AGENDADO' || item.STATUS === 'REAGENDADO')
        .reduce((acc: any, item) => {
          if (item.PROFISSIONAL) {
            acc[item.PROFISSIONAL] = (acc[item.PROFISSIONAL] || 0) + 1;
          }
          return acc;
        }, {}) || {};

      const cores = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];
      const performanceData = Object.entries(profissionalCounts)
        .map(([name, count], index) => ({
          name,
          agendamentos: count,
          color: cores[index % cores.length]
        }));

      return { statusData, horariosPicoData, performanceData };
    },
  });
};